import { type AlgNode, Move, Newline } from '@vscubing/cubing/alg'
import { puzzles } from '@vscubing/cubing/puzzles'
import {
  type AnimationTimelineLeaf,
  type ExperimentalLeafIndex,
  type ExperimentalMillisecondTimestamp,
  getSolveAnalyzer,
} from '@vscubing/cubing/twisty'
import { Alg, LineComment } from '@vscubing/cubing/alg'
import type { Discipline } from '@/types'
import { formatSolveTime } from '@/lib/utils/format-solve-time'
import { removeSolutionComments } from '@/lib/utils/remove-solution-comments'
import { isRotation } from '@/lib/utils/is-rotation'

// TODO: simultaneous moves???
export async function doEverything(
  scramble: string,
  solutionWithTimings: string,
  discipline: Discipline,
): Promise<{
  solution: Alg
  animLeaves?: AnimationTimelineLeaf[]
  startIndex: ExperimentalLeafIndex
}> {
  const timestamps = parseTimestamps(solutionWithTimings)

  let solution = new Alg(removeSolutionComments(solutionWithTimings))

  const startIndex = Math.max(
    Array.from(solution.childAlgNodes()).findIndex(
      (node) => !isRotation(node),
    ) - 1,
    0,
  ) as ExperimentalLeafIndex

  const rawSignatures = await ANALYZER_MAP[discipline](scramble, solution)
  const signaturesWithDurations = embedDurations(rawSignatures, timestamps)

  solution = annotateMoves(solution, signaturesWithDurations)

  if (!timestamps) {
    return { solution, startIndex }
  }

  const animatableNodes = Array.from(solution.childAlgNodes()).filter((node) =>
    node.is(Move),
  )

  if (animatableNodes.length !== timestamps?.length) {
    console.error('[TWISTY] animatableNodes.length !== leafSlots.length')
    return { solution: solution, startIndex }
  }

  const animLeaves: AnimationTimelineLeaf[] = animatableNodes.map(
    (node, idx) => ({
      start: timestamps[idx]!,
      end: Math.min(
        timestamps[idx]! + 120,
        timestamps[idx + 1] ?? Infinity,
      ) as ExperimentalMillisecondTimestamp,
      animLeaf: node,
    }),
  )

  return { solution, animLeaves, startIndex }
}

function embedDurations(
  rawSignatures: (string | null)[],
  timestamps?: number[],
) {
  let lastIdxWithSignature = -1
  return rawSignatures.map((signature, idx) => {
    if (signature === null) return null
    let comment = ` ${signature}`

    if (timestamps) {
      const stepDuration =
        lastIdxWithSignature === -1
          ? timestamps[idx]!
          : timestamps[idx]! - timestamps[lastIdxWithSignature]!
      lastIdxWithSignature = signature === INSPECTION_SIGNATURE ? idx + 1 : idx
      if (stepDuration > 0 && signature !== INSPECTION_SIGNATURE) {
        comment += ` (${formatSolveTime(stepDuration, true)}s)`
      }
    }

    return comment
  })
}

function annotateMoves(
  solution: Alg,
  signaturesWithDurations: (string | null)[],
) {
  const res: AlgNode[] = []
  Array.from(solution.childAlgNodes()).forEach((node, idx) => {
    res.push(node)

    const comment = signaturesWithDurations[idx]
    if (comment) {
      res.push(new LineComment(comment))
      res.push(new Newline())
    }
  })
  return new Alg(res)
}

function parseTimestamps(
  solutionWithTimestamps: string,
): ExperimentalMillisecondTimestamp[] | undefined {
  const timestamps = solutionWithTimestamps
    .split('*')
    .filter((_, idx) => idx % 2 === 1)
    .map(Number)
  if (timestamps.length === 0) return undefined

  const hasInspection = timestamps[0] === 0 && timestamps[1] === 0
  if (!hasInspection) return timestamps as ExperimentalMillisecondTimestamp[]

  let shift = 0
  return timestamps.map((timestamp, idx) => {
    if (timestamp === 0 && idx > 0) shift += 300
    return (timestamp + shift) as ExperimentalMillisecondTimestamp
  })
}

const placeholderAnalyzer: Analyzer = (_, solution) => {
  const signatures: (string | null)[] = []

  let inInspection = true
  let solutionSoFar = new Alg()
  for (const node of new Alg(solution).childAlgNodes()) {
    solutionSoFar = new Alg([...solutionSoFar.childAlgNodes(), node])

    signatures.push(null)

    if (!isRotation(node) && inInspection) {
      inInspection = false
      if (signatures.length > 1) {
        signatures[signatures.length - 2] = INSPECTION_SIGNATURE
      }
    }
  }

  signatures[signatures.length - 1] = 'Solved'

  return new Promise((res) => res(signatures))
}

type Analyzer = (
  scramble: Alg | string,
  solution: Alg | string,
) => Promise<(string | null)[]>
const ANALYZER_MAP: Record<Discipline, Analyzer> = {
  '3by3': async (scramble, solution) => {
    const puzzleLoader = puzzles['3x3x3']!
    const kpuzzle = await puzzleLoader.kpuzzle()
    const solved = kpuzzle.defaultPattern()
    const analyzeSolve = await getSolveAnalyzer(puzzleLoader)

    const signatures: (string | null)[] = []

    let inInspection = true
    let solutionSoFar = new Alg()
    for (const [idx, node] of Array.from(
      new Alg(solution).childAlgNodes(),
    ).entries()) {
      solutionSoFar = new Alg([...solutionSoFar.childAlgNodes(), node])

      const pattern = solved.applyAlg(scramble).applyAlg(solutionSoFar)
      signatures.push(analyzeSolve(pattern))

      if (!isRotation(node) && inInspection) {
        inInspection = false
        if (idx > 0) {
          signatures[signatures.length - 2] = INSPECTION_SIGNATURE
        }
      }
    }
    return signatures
  },
  '2by2': placeholderAnalyzer,
  '4by4': placeholderAnalyzer,
}

const INSPECTION_SIGNATURE = 'Inspection'
